import React, { useState } from 'react';
import { PlusIcon, Edit2Icon, Trash2Icon, SaveIcon, XIcon, ArrowUpIcon, ArrowDownIcon, PinIcon } from 'lucide-react';
import { useWriting } from '../../lib/cmsStore';
import { WritingPost } from '../../lib/cmsTypes';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { Select } from '../../components/ui/Select';
import { RichTextEditor } from '../../components/admin/RichTextEditor';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
export function WritingEditor() {
  const {
    items,
    saveItem,
    deleteItem,
    reorderItems,
    isLoading
  } = useWriting();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<WritingPost | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  if (isLoading) return <div className="p-8 font-mono text-sm" data-id="element-1626">Loading...</div>;
  const sortedItems = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
  const handleEdit = (item: WritingPost) => {
    setEditForm({
      ...item
    });
    setEditingId(item.id);
  };
  const handleCreate = () => {
    const newItem: WritingPost = {
      id: Math.random().toString(36).substr(2, 9),
      title: 'New Article',
      subtitle: '',
      slug: 'new-article-' + Date.now(),
      featuredImage: '',
      excerpt: '',
      content: '<p>Start writing...</p>',
      tags: [],
      categories: [],
      author: 'Sinka',
      publishDate: new Date().toISOString(),
      readingTime: '5 min',
      featured: false,
      status: 'draft',
      seoTitle: '',
      seoDescription: '',
      ogImage: '',
      order: items.length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setEditForm(newItem);
    setEditingId(newItem.id);
  };
  const handleSave = () => {
    if (editForm) {
      saveItem({
        ...editForm,
        updatedAt: new Date().toISOString()
      });
      setEditingId(null);
      setEditForm(null);
    }
  };
  const updateField = (field: keyof WritingPost, value: any) => {
    setEditForm(prev => prev ? {
      ...prev,
      [field]: value
    } : prev);
  };
  const togglePin = (item: WritingPost) => {
    saveItem({
      ...item,
      featured: !item.featured,
      updatedAt: new Date().toISOString()
    });
  };
  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === sortedItems.length - 1) return;
    const newItems = [...sortedItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newItems[index];
    newItems[index] = newItems[targetIndex];
    newItems[targetIndex] = temp;
    const reorderedItems = newItems.map((item, i) => ({
      ...item,
      order: i
    }));
    reorderItems(reorderedItems);
  };
  const handleHistoryRestore = (data: any) => {
    if (Array.isArray(data)) {
      reorderItems(data);
    }
  };
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-1627">
      <div className="flex items-center justify-between mb-8" data-id="element-1628">
        <div data-id="element-1629">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-1630">
            Writing
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-1631">
            Manage your articles and thoughts.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-1632">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-1633" />
          {!editingId && <Button onClick={handleCreate} className="gap-2" data-id="element-1634">
              <PlusIcon className="w-4 h-4" data-id="element-1635" /> New Article
            </Button>}
        </div>
      </div>

      {editingId && editForm ? <div className="border border-border bg-card p-6 md:p-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-1636">
          <div className="flex items-center justify-between border-b border-border pb-4" data-id="element-1637">
            <h2 className="font-display text-2xl font-bold" data-id="element-1638">Edit Article</h2>
            <div className="flex gap-2" data-id="element-1639">
              <Button variant="ghost" onClick={() => setEditingId(null)} data-id="element-1640">
                <XIcon className="w-4 h-4 mr-2" data-id="element-1641" /> Cancel
              </Button>
              <Button onClick={handleSave} data-id="element-1642">
                <SaveIcon className="w-4 h-4 mr-2" data-id="element-1643" /> Save
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1644">
            <Input label="Title" value={editForm.title} onChange={e => updateField('title', e.target.value)} data-id="element-1645" />
            <Input label="Slug" value={editForm.slug} onChange={e => updateField('slug', e.target.value)} data-id="element-1646" />
            <Input label="Subtitle" value={editForm.subtitle} onChange={e => updateField('subtitle', e.target.value)} data-id="element-1647" />
            <Select label="Status" value={editForm.status} onChange={e => updateField('status', e.target.value)} options={[{
          label: 'Draft',
          value: 'draft'
        }, {
          label: 'Published',
          value: 'published'
        }]} data-id="element-1648" />
            <Input label="Reading Time (e.g. 5 min)" value={editForm.readingTime} onChange={e => updateField('readingTime', e.target.value)} data-id="element-1649" />
            <Input label="Publish Date" type="date" value={editForm.publishDate.split('T')[0]} onChange={e => updateField('publishDate', new Date(e.target.value).toISOString())} data-id="element-1650" />
            <Input label="Tags (comma-separated)" value={editForm.tags?.join(', ') || ''} onChange={e => updateField('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} data-id="element-1651" />
            <Input label="Categories (comma-separated)" value={editForm.categories?.join(', ') || ''} onChange={e => updateField('categories', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} data-id="element-1652" />
            <div className="flex items-center h-10 mt-6" data-id="element-1653">
              <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-foreground cursor-pointer" data-id="element-1654">
                <input type="checkbox" checked={editForm.featured} onChange={e => updateField('featured', e.target.checked)} className="accent-primary w-4 h-4" data-id="element-1655" />
                Pin to top
              </label>
            </div>
          </div>

          <Textarea label="Excerpt" value={editForm.excerpt} onChange={e => updateField('excerpt', e.target.value)} rows={2} data-id="element-1656" />

          <Input label="Featured Image URL" value={editForm.featuredImage} onChange={e => updateField('featuredImage', e.target.value)} data-id="element-1657" />

          {/* Rich Text Editor for Content */}
          <RichTextEditor label="Content" value={editForm.content} onChange={html => updateField('content', html)} minHeight="400px" data-id="element-1658" />
        </div> : <div className="bg-card border border-border" data-id="element-1659">
          <div className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 p-4 border-b border-border font-mono text-xs uppercase tracking-widest text-muted-foreground" data-id="element-1660">
            <div className="w-16" data-id="element-1661">Order</div>
            <div data-id="element-1662">Article</div>
            <div className="w-32" data-id="element-1663">Date</div>
            <div className="w-24" data-id="element-1664">Status</div>
            <div className="w-12 text-center" data-id="element-1665">Pin</div>
            <div className="w-32 text-right" data-id="element-1666">Actions</div>
          </div>

          <div className="divide-y divide-border" data-id="element-1667">
            {sortedItems.length === 0 ? <div className="p-8 text-center text-muted-foreground font-mono text-sm" data-id="element-1668">
                No articles found.
              </div> : sortedItems.map((item, index) => <div key={item.id} className="grid grid-cols-[auto_1fr_auto_auto_auto_auto] gap-4 p-4 items-center hover:bg-secondary/50 transition-colors" data-id="element-1669">
                  <div className="w-16 flex flex-col gap-1" data-id="element-1670">
                    <button onClick={() => moveItem(index, 'up')} disabled={index === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1671">
                      <ArrowUpIcon className="w-4 h-4" data-id="element-1672" />
                    </button>
                    <button onClick={() => moveItem(index, 'down')} disabled={index === sortedItems.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-1673">
                      <ArrowDownIcon className="w-4 h-4" data-id="element-1674" />
                    </button>
                  </div>
                  <div data-id="element-1675">
                    <h3 className="font-display text-lg font-bold" data-id="element-1676">
                      {item.title}
                    </h3>
                    <p className="font-mono text-xs text-muted-foreground mt-1 truncate max-w-md" data-id="element-1677">
                      {item.excerpt}
                    </p>
                  </div>
                  <div className="w-32 font-mono text-xs text-muted-foreground" data-id="element-1678">
                    {new Date(item.publishDate).toLocaleDateString()}
                  </div>
                  <div className="w-24 flex flex-col gap-1" data-id="element-1679">
                    <span className={`inline-flex items-center justify-center px-2 py-1 font-mono text-[10px] uppercase tracking-widest border ${item.status === 'published' ? 'border-primary/50 text-primary bg-primary/10' : 'border-border text-muted-foreground bg-secondary'}`} data-id="element-1680">
                      {item.status}
                    </span>
                  </div>
                  <div className="w-12 flex justify-center" data-id="element-1681">
                    <button onClick={() => togglePin(item)} className={`p-1.5 rounded-md transition-colors ${item.featured ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:bg-secondary'}`} title={item.featured ? 'Unpin' : 'Pin to top'} data-id="element-1682">
                      <PinIcon className="w-4 h-4" data-id="element-1683" />
                    </button>
                  </div>
                  <div className="w-32 flex justify-end gap-2" data-id="element-1684">
                    <Button variant="ghost" size="sm" className="px-2" onClick={() => handleEdit(item)} title="Edit" data-id="element-1685">
                      <Edit2Icon className="w-4 h-4" data-id="element-1686" />
                    </Button>
                    <Button variant="ghost" size="sm" className="px-2 text-destructive hover:text-destructive" onClick={() => {
              if (window.confirm('Delete this article?')) deleteItem(item.id);
            }} title="Delete" data-id="element-1687">
                      <Trash2Icon className="w-4 h-4" data-id="element-1688" />
                    </Button>
                  </div>
                </div>)}
          </div>
        </div>}

      <VersionHistoryPanel contentKey="sinka_writing" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={handleHistoryRestore} data-id="element-1689" />
    </div>;
}